/**
 * macOS-style dialog transition: a dialog grows out of the element that was clicked to open it
 * and shrinks back into the same spot when it closes.
 * Used as the default `transition` of every VDialog (see defaults.ts); dialogs that set their own
 * transition (e.g. side drawers) are unaffected.
 */
import { Transition, h } from 'vue';
// A click older than this is assumed not to be what opened the dialog (opened via keyboard, timer, API result...)
const CLICK_MAX_AGE_MS = 3000;

let lastClick: { x: number; y: number; time: number } | null = null;

if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    e => {
      lastClick = { x: e.clientX, y: e.clientY, time: Date.now() };
    },
    true
  );
}

const origins = new WeakMap<HTMLElement, { x: number; y: number }>();

const setOrigin = (el: HTMLElement, origin: { x: number; y: number }) => {
  el.style.setProperty('--genie-x', `${origin.x}px`);
  el.style.setProperty('--genie-y', `${origin.y}px`);
};

// Measured in onEnter, not onBeforeEnter: VOverlay toggles its content with v-show, so the element is still
// display:none (zero size) during beforeEnter. onEnter runs after it is shown but before the first animated frame.
const onEnter = (el: Element) => {
  const content = el as HTMLElement;

  // Measure the natural position, ignoring the scaled/translated "enter-from" state
  // Transitions are disabled meanwhile, and a style flush is forced after restoring, so the browser's
  // "before" state for the real animation is the small enter-from state (otherwise it first animates
  // from full size down to small, which shows up as a jump).
  content.style.transition = 'none';
  content.style.transform = 'none';

  const rect = content.getBoundingClientRect();

  let origin = { x: 0, y: 0 };

  if (lastClick && Date.now() - lastClick.time < CLICK_MAX_AGE_MS && rect.width > 0) {
    // translate() distance from the dialog's own center to the clicked point
    origin = {
      x: lastClick.x - (rect.left + rect.width / 2),
      y: lastClick.y - (rect.top + rect.height / 2)
    };
  }

  origins.set(content, origin);
  setOrigin(content, origin);

  content.style.transform = '';
  void getComputedStyle(content).transform;
  content.style.transition = '';
};

const onBeforeLeave = (el: Element) => {
  const content = el as HTMLElement;

  setOrigin(content, origins.get(content) ?? { x: 0, y: 0 });
};

export const GenieDialogTransition = (_props: Record<string, unknown>, { slots }: { slots: any }) =>
  h(Transition, { name: 'genie-dialog', appear: true, onEnter, onBeforeLeave }, slots);

GenieDialogTransition.inheritAttrs = false;
